import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Input, OnChanges, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { EnumMeasureTypes, Measure, TimeRange } from '@library/store/measures/measures.interface';
import { BarChart, LineChart, Svg } from 'chartist';
import * as moment from 'moment';

@Component({
  selector: 'app-preview-graph',
  templateUrl: './preview-graph.component.html',
  styleUrls: ['./preview-graph.component.scss'],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PreviewGraphComponent implements OnChanges, AfterViewInit {
  id = `ct-${Math.round(Math.random() * 1000000)}`;

  @Input() data: Measure[][];
  @Input() color: string;
  @Input() measureType: EnumMeasureTypes;

  private chart: LineChart | BarChart;

  @ViewChild('preview') preview: ElementRef<HTMLDivElement>;

  ngOnChanges(sc: SimpleChanges) {
    if (this.preview && sc.data) {
      this.createPreview(this.data);
    }
  }

  ngAfterViewInit() {
    this.createPreview(this.data);
  }

  async createPreview(measuresArray: Measure[][] = []) {
    if (!measuresArray) {
      return;
    }

    let data: Measure[] = [];

    switch (this.measureType) {
      case EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_BASE:
      case EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PRICE_BASE:
      case EnumMeasureTypes.SUM_FLUID_CONSUMPTION_GAS:
      case EnumMeasureTypes.SUM_FLUID_PRICE_GAS:
      case EnumMeasureTypes.SUM_FLUID_CONSUMPTION_COLD_WATER:
      case EnumMeasureTypes.SUM_FLUID_PRICE_COLD_WATER:
      case EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_NO_CONTRACT:
      case EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PEAK:
      case EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PRICE_PEAK:
      case EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_OFFPEAK:
      case EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PRICE_OFFPEAK:
      case EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_SUPER_OFFPEAK:
      case EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PRICE_SUPER_OFFPEAK:
      case EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_WEEKEND:
      case EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PRICE_WEEKEND:
      case EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_FREE:
      case EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PRICE_FREE:
      case EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_CUSTOM1:
      case EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PRICE_CUSTOM1:
      case EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_CUSTOM2:
      case EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PRICE_CUSTOM2:
      case EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_CUSTOM3:
      case EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PRICE_CUSTOM3:
      case EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_CUSTOM4:
      case EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PRICE_CUSTOM4:
      case EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_CUSTOM5:
      case EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PRICE_CUSTOM5:
      case EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_CUSTOM6:
      case EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PRICE_CUSTOM6:
      case EnumMeasureTypes.SUM_ENERGY_SELF_CONSUMPTION:
        data = addMeasures(measuresArray);
        this.chart = new BarChart(
          `#${this.id}`,
          {
            series: [data],
          },
          CHART_OPTIONS,
        );
        break;
      default: {
        data = measuresArray.flat();
        this.chart = new LineChart(
          `#${this.id}`,
          {
            series: [data],
          },
          CHART_OPTIONS,
        );
        break;
      }
    }

    this.chart.on('created', (data) => {
      const defs = new Svg('defs');
      const gradient = defs.elem('linearGradient', { id: `${this.id}-area-gradient]`, x1: '0%', y1: '0%', x2: '0%', y2: '100%' });

      gradient.elem('stop', { offset: 0, 'stop-color': this.color });
      gradient.elem('stop', { offset: 20, 'stop-color': this.color, 'stop-opacity': '0' });

      data.svg.append(defs, true);

      data.svg.querySelector('.ct-series')
        .removeClass('ct-series-a')
        .attr({
          stroke: this.color,
        });

      if (data.svg.querySelector('.ct-series .ct-area')) {
        data.svg.querySelector('.ct-series .ct-area')
          .attr({
            fill: `url(#${this.id}-area-gradient])`,
          });
      }

      // Set the min height of the bars to 2 pixels
      data.svg.querySelectorAll('.ct-series .ct-bar').svgElements.forEach(e => {
        if (e._node.y1.baseVal.value === e._node.y2.baseVal.value) {
          e._node.y2.baseVal.value = e._node.y1.baseVal.value - 2;
        }
      });
    });
  }
}

/**
 * [
 *  [], // GRID_BASE
 *  [ {x: 0, y: 0}, ... ], // GRID_PEAK
 *  [ {x: 0, y: 0}, ... ], // GRID_OFFPEAK
 *  [], // GRID_SUPER_OFFPEAK
 * ]
 */

function addMeasures(measuresArray: Measure[][]) {
  const flatMeasures = measuresArray.flat();

  // create an object with all the existing x values at first
  const summedMeasures = flatMeasures.reduce((acc, m) => {
    acc[m.x] = { x: m.x, y: 0 };
    return acc;
  }, {} as Record<number, Measure>);

  // sum all the y values that have the same x
  flatMeasures.forEach(element => {
    summedMeasures[element.x].y += element.y;
  });

  // sort the measures by x
  const sortedMeasures = Object.values(summedMeasures).sort((a, b) => a.x - b.x);

  // create an array of 24 measures with 30 minutes intervals
  const finalMeasures = Array.from(Array(24), (_, i) => ({ x: getCurrentTimeRange().dateBegin.clone().add(i * 30, 'minutes').valueOf(), y: 0 } as Measure));

  // fill each 30 minute interval with the existing measures or 0 if there is no measure
  return finalMeasures.map((m) => {
    return {
      ...m,
      y: sortedMeasures.find(measure => measure.x === m.x)?.y || 0,
    };
  });
}

const CHART_OPTIONS = {
  showArea: true,
  showPoint: false,
  fullWidth: true,
  height: '60px',
  width: '140px',
  chartPadding: {
    left: 0,
    right: 0,
  },
  axisX: {
    showLabel: false,
    showGrid: false,
    offset: 0,
  },
  axisY: {
    showLabel: false,
    showGrid: false,
    offset: 0,
  },
};

export function getCurrentTimeRange(): TimeRange {
  const date = moment();
  // round the date to the nearest 30 minutes
  const roundedDate = date.clone().startOf('hour').add(Math.floor(date.minute() / 30) * 30, 'minutes');

  return {
    dateBegin: roundedDate.clone().subtract(12, 'hours'),
    dateEnd: roundedDate.clone(),
  };
}
